/**
 * Signature help
 *
 * Triggers in two positions:
 *   - After `<Module>.`           → list interfaces of that module
 *   - After `>`                   → list every interface, qualified
 *
 * The `activeSignature` defaults to 0 — IDEs display all signatures and
 * let the user cycle through them.
 */

import {
  type ParameterInformation,
  type SignatureHelp,
  type SignatureInformation,
} from "vscode-languageserver";
import type { SymbolEntry, SymbolTable } from "../workspace/symbol-table.js";
import type { Workspace } from "../workspace/workspace.js";

export function provideSignatureHelp(
  workspace: Workspace,
  symbols: SymbolTable,
  uri: string,
  line: number,
  character: number
): SignatureHelp | null {
  const file = workspace.getFile(uri);
  if (!file) return null;

  const lineText = lineAt(file.text, line);
  const beforeCursor = lineText.slice(0, character);

  // After the call arrow `>` → all interfaces.
  if (/>\s*$/.test(beforeCursor)) {
    return signaturesFromInterfaces(allInterfaces(symbols), "interfaces");
  }

  // After `<Module>.` → that module's interfaces.
  const dotMatch = /([A-Za-z_][A-Za-z0-9_.]*)\.\s*$/.exec(beforeCursor);
  if (dotMatch) {
    const qname = dotMatch[1]!;
    // Referrer-aware (v0.13): a twin module name resolves to THIS file's space.
    const moduleSym = symbols.resolve(qname, uri);
    if (!moduleSym || (moduleSym.kind !== "module" && moduleSym.kind !== "surface")) {
      return null;
    }
    const interfaces = symbols.interfacesOf(qname);
    if (interfaces.length === 0) return null;
    const sigs: SignatureInformation[] = interfaces.map((iface) => ({
      label: `${moduleSym.name}.${iface.name} (${iface.typeName ?? "interface"})`,
      documentation: iface.description,
      parameters: [] as ParameterInformation[],
    }));
    return { signatures: sigs, activeSignature: 0, activeParameter: 0 };
  }

  return null;
}

// =============================================================================
// Helpers
// =============================================================================

function allInterfaces(symbols: SymbolTable): SymbolEntry[] {
  return symbols.all().filter((s) => s.kind === "interface");
}

function signaturesFromInterfaces(
  ifaces: readonly SymbolEntry[],
  fallbackLabel: string
): SignatureHelp | null {
  if (ifaces.length === 0) return null;
  const sigs: SignatureInformation[] = ifaces
    .slice() // copy before sort
    .sort((a, b) => a.qualifiedName.localeCompare(b.qualifiedName))
    .map((iface) => ({
      label: `${iface.qualifiedName} (${iface.typeName ?? fallbackLabel})`,
      documentation: iface.description,
      parameters: [] as ParameterInformation[],
    }));
  return { signatures: sigs, activeSignature: 0, activeParameter: 0 };
}

function lineAt(text: string, line: number): string {
  let start = 0;
  let n = 0;
  for (let i = 0; i < text.length; i++) {
    if (n === line) {
      start = i;
      break;
    }
    if (text[i] === "\n") n++;
  }
  if (n !== line && n !== line - 1) return "";
  let end = start;
  while (end < text.length && text[end] !== "\n") end++;
  return text.slice(start, end);
}
